import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import {
  fetchUserById,
  clearSelectedEntity,
  selectSelectedUser,
  selectUserDetailStatus,
  selectUserDetailError,
} from '../../features/users/userSlice';
import { selectUserInformations } from '../../features/authSlice';
import { InputField } from '../../components/reusable/InputField';
import ToggleSwitch from '../../components/reusable/ToggleSwitch';
import Datepicker from 'react-tailwindcss-datepicker';
import userFields from '../../config/userFields';
import { showToast } from '../../utils/utils';
import { formatPhoneNumberForDisplay } from '../../utils/phoneUtils';
import Loader from '../../components/Live/Loader';
import { FiEdit, FiArrowLeft, FiChevronDown, FiChevronUp } from 'react-icons/fi';

/**
 * Preview User Page
 * Full page read-only view of user details
 * Replaces the modal-based preview flow
 */
const PreviewUserPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedUser = useSelector(selectSelectedUser);
  const detailStatus = useSelector(selectUserDetailStatus);
  const detailError = useSelector(selectUserDetailError);

  const [searchQueries, setSearchQueries] = useState({});
  const roles = useMemo(() => selectedUser?.roles || [], [selectedUser?.roles]);

  const handleSearchChange = useCallback((roleUuid, value) => {
    setSearchQueries(prev => ({ ...prev, [roleUuid]: value }));
  }, []);

  // Fetch user data on mount
  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchUserById({ token: user.token, userId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  // Handle fetch errors
  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/users', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Error state
  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/users')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  // No user data
  if (!selectedUser) {
    return null;
  }

  // Filter fields for preview
  const nonFileFields = userFields.filter(field =>
    field.type !== 'file' &&
    field.type !== 'toggle' &&
    field.type !== 'textarea' &&
    field.type !== 'select'
  );

  const fileField = userFields.find(field => field.type === 'file');
  const toggleField = userFields.find(field => field.type === 'toggle');
  const textareaField = userFields.find(field => field.type === 'textarea');
  const selectField = userFields.filter(field => field.type === 'select');
  const datepickerField = userFields.find(field => field.type === 'datepicker');
  const radioFields = userFields.filter(field => field.type === 'radio');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  const imageVariants = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/users"
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  User Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View user information
                </p>
              </div>
            </div>
            <Link
              to={`/dashboard/users/${id}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit className="w-4 h-4" />
              <span>Edit User</span>
            </Link>
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Non-file Fields */}
            {nonFileFields.map((field, index) => {
              // Format phone number for display
              const displayValue = field.type === 'phone' 
                ? formatPhoneNumberForDisplay(selectedUser[field.name])
                : (selectedUser[field.name] || '');
              
              return (
                <motion.div
                  key={field.name}
                  variants={itemVariants}
                  transition={{ delay: index * 0.05 }}
                >
                  <InputField
                    label={field.label}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={displayValue}
                    disabled={true}
                  />
                </motion.div>
              );
            })}

            {/* Radio Fields */}
            {radioFields.map((field, index) => (
              <motion.div
                key={field.name}
                variants={itemVariants}
                transition={{ delay: (nonFileFields.length + index) * 0.05 }}
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {field.label}
                </label>
                <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                  <span className="text-gray-900 dark:text-white capitalize">
                    {field.options?.find(opt => opt.value === selectedUser[field.name])?.label || selectedUser[field.name]}
                  </span>
                </div>
              </motion.div>
            ))}

            {/* Datepicker Field */}
            <AnimatePresence>
              {datepickerField && selectedUser[datepickerField.name] && (
                <motion.div
                  key={datepickerField.name}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {datepickerField.label}
                  </label>
                  <Datepicker
                    inputClassName="rounded-lg border-2 w-full p-2 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    readOnly={true}
                    asSingle={true}
                    useRange={false}
                    primaryColor='blue'
                    disabled={true}
                    value={{
                      startDate: new Date(selectedUser[datepickerField.name]),
                      endDate: new Date(selectedUser[datepickerField.name])
                    }}
                    toggleClassName="absolute bg-blue-600 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Toggle Switch Field */}
            <AnimatePresence>
              {toggleField && (
                <motion.div
                  key={toggleField.name}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <ToggleSwitch
                    label={toggleField.label}
                    checked={selectedUser[toggleField.name] === 'active' || !!selectedUser[toggleField.name]}
                    className="space-x-2"
                    disabled={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Textarea Field */}
            <AnimatePresence>
              {textareaField && (
                <motion.div
                  key={textareaField.name}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="sm:col-span-2"
                >
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {textareaField.label}
                  </label>
                  <textarea
                    name={textareaField.name}
                    className="w-full h-32 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none overflow-y-auto dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={textareaField.placeholder}
                    value={selectedUser[textareaField.name] || ''}
                    disabled={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Image Preview */}
          {fileField && selectedUser[fileField.name] && (
            <motion.div
              className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700"
              variants={imageVariants}
            >
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {fileField.label}
              </label>
              <div className="flex justify-center sm:justify-start">
                <img
                  className="w-full sm:w-auto sm:max-w-md h-auto max-h-96 object-cover rounded-lg shadow-lg"
                  src={selectedUser[fileField.name]}
                  alt="User"
                />
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PreviewUserPage;
